/*
Impression and interactions of a single card (Matomo content tracking, see
src/utils/matomo.ts and components/base/ContentTracking.tsx).

The impression runs through an IntersectionObserver instead of Matomo's
`trackVisibleContentImpressions`: Matomo's visibility check knows no threshold, it
counts from a single visible pixel on. What is required is 80 %.
*/

'use client';

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useContentName } from '@/components/base/ContentTracking';
import {
  MatomoContentBlock,
  claimContentImpression,
  trackContentImpression,
  trackContentInteraction,
} from '@/utils/matomo';

/** Share of the card that has to be visible for it to count as seen. */
const IMPRESSION_RATIO = 0.8;

/*
Thresholds in 5 % steps instead of just `[0.8]`: if a card is taller than the viewport,
its `intersectionRatio` never reaches 0.8 and with a single threshold the observer would
fall silent after the first callback. With the intermediate steps it keeps reporting, and
the fallback below decides on viewport coverage.
*/
const IMPRESSION_THRESHOLDS = Array.from({ length: 21 }, (_, step) => step / 20);

function isSeen(entry: IntersectionObserverEntry): boolean {
  if (entry.intersectionRatio >= IMPRESSION_RATIO) return true;

  // Card taller than the viewport: seen as soon as it fills 80 % of the viewport height.
  const rootHeight = entry.rootBounds?.height ?? window.innerHeight;

  return rootHeight > 0 && entry.intersectionRect.height / rootHeight >= IMPRESSION_RATIO;
}

/**
 * Connects a card to content tracking.
 *
 * The content name comes from the surrounding `ContentTrackingModule`. Without it the
 * hook is a no-op: no observer, `block` is `undefined` and `trackInteraction` sends
 * nothing. So cards only track on pages that have wrapped their modules.
 *
 * @param contentPiece see `buildContentPiece`, e.g. `Event - <slug>`
 * @param contentTarget path the card leads to
 * @returns `ref` for the card's root, the `block` for `ContentBlockProvider` and
 *   `trackInteraction` for click handlers
 */
export function useContentImpression({
  contentPiece,
  contentTarget,
}: {
  contentPiece: string;
  contentTarget: string;
}) {
  const contentName = useContentName();
  const ref = useRef<HTMLDivElement | null>(null);

  const block: MatomoContentBlock | undefined = useMemo(
    () => (contentName ? { contentName, contentPiece, contentTarget } : undefined),
    [contentName, contentPiece, contentTarget],
  );

  useEffect(() => {
    const element = ref.current;
    if (!element || !block) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!isSeen(entry)) return;

          // Once seen stays seen — the rest is work without effect.
          observer.unobserve(entry.target);

          if (claimContentImpression(block)) {
            trackContentImpression(block);
          }
        });
      },
      { threshold: IMPRESSION_THRESHOLDS },
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [block]);

  const trackInteraction = useCallback(
    (interaction: string) => {
      if (block) trackContentInteraction(interaction, block);
    },
    [block],
  );

  return { ref, block, trackInteraction };
}
